/** biome-ignore-all lint/correctness/noUndeclaredDependencies: <needs to fallback to old code> */
'use client';

import cn from 'classnames';
import { useServerInsertedHTML } from 'next/navigation';
// biome-ignore lint/performance/noNamespaceImport: need it for styled-components
import * as S from 'pages/Article/V2/ArticleStyles';
import { useMemo, useState } from 'react';
import { ServerStyleSheet, StyleSheetManager, ThemeProvider } from 'styled-components';
import theme from 'styles/theme';
import { ParseArticleContent } from 'utils/miscUtils/rawHtml';
import { applyRulesToReactTree } from '@/app_lib/htmlParser/inserter/inserter';
import { clientRuleBuilder } from '@/app_lib/htmlParser/inserter/rules/clientRuleBuilder';
import { insertionRules } from '@/app_lib/htmlParser/inserter/rules/helpers';
import type { ClientParserData } from '@/app_lib/htmlParser/inserter/rules/types';
import { AnalyticsEvent, ContentPlacement } from '../types/analytics';

type Props = {
  content: string;
  clientData?: ClientParserData[]; // Data for client-side rules
};

export const ClientParser = ({ content, clientData = [] }: Props) => {
  // https://styled-components.com/docs/advanced#server-side-rendering
  const [sheet] = useState(() => new ServerStyleSheet());

  // This hook will insert the styled components styles before streamed HTML is sent to the client.
  useServerInsertedHTML(() => {
    const styles = sheet.getStyleElement();
    sheet.instance.clearTag(); // Prevents memory leaks
    return <>{styles}</>;
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <only run on mount>
  const insertedContent = useMemo(() => {
    const rules = clientRuleBuilder(clientData);
    const parsedContent = ParseArticleContent(content, false);
    const inserted = applyRulesToReactTree({
      elements: parsedContent,
      rules: [...insertionRules, ...rules],
    });
    return inserted;
  }, []);

  return (
    <ThemeProvider theme={theme('own')}>
      <StyleSheetManager
        enableVendorPrefixes
        sheet={sheet.instance}
      >
        <S.ArticleContentWrapper>
          <div
            className={cn('client-article-parser', AnalyticsEvent.ImpressionElement)}
            data-cy={ContentPlacement.ArticleContent}
            suppressHydrationWarning
          >
            {insertedContent}
          </div>
        </S.ArticleContentWrapper>
      </StyleSheetManager>
    </ThemeProvider>
  );
};
